import {
  type Facet,
  isChoiceFacet,
  isTermsFacet,
} from "@/features/search/api/types";

/* Identical on a facet and on a saved filter, so one formatter serves the chips and the dashboard. */
export type RangeUnit = Extract<Facet, { type: "range" }>["unit"];

/** "From"/"To" in the caller's language — a range with one bound reads as a sentence, not a dash. */
export type RangeWords = { from: string; to: string };

/* The mark the inputs carry, so a chip describes the same number the same way: €100, 50 cm. */
const withUnit = (value: number, unit: RangeUnit): string =>
  unit === null
    ? String(value)
    : unit.position === "prefix"
    ? `${unit.symbol}${value}`
    : `${value} ${unit.symbol}`;

/**
 * A range as one phrase: both bounds as "100 – 500", a lone lower bound as "From 100", a lone upper
 * one as "To 500". The old form padded the missing side with an ellipsis ("100 – …"), which read as
 * a truncation rather than as an open end.
 */
export const describeRange = (
  min: number | null | undefined,
  max: number | null | undefined,
  unit: RangeUnit,
  words: RangeWords
): string => {
  if (min != null && max != null) {
    return `${withUnit(min, unit)} – ${withUnit(max, unit)}`;
  }

  return min != null
    ? `${words.from} ${withUnit(min, unit)}`
    : `${words.to} ${withUnit(max ?? 0, unit)}`;
};

/*
 * A term chip normally names itself — "Tableware", "Green". A free-shipping country does not:
 * "Netherlands" is the same word the Country facet's own chip uses, so on its own it says which
 * country, not what about it. It takes its facet name along, in the "<facet>: <value>" form the
 * range chips already use, and reuses the section heading rather than inventing a second string.
 */
export const describeTerms = (
  facetKey: string,
  values: string,
  facetLabel: (key: string) => string
) =>
  facetKey === "free_shipping" ? `${facetLabel(facetKey)}: ${values}` : values;

export type AppliedFilter = {
  /** Unique per entry: `facetKey:value` for a term, the bare key for a range or choice. */
  key: string;
  facetKey: string;
  /** How to clear it — a term toggles off, a range clears its bounds, a choice drops its param. */
  kind: "terms" | "range" | "choice";
  /** Absent on a range — the label carries both bounds instead. */
  value?: string;
  /** Choice only: the query param to drop in order to clear it, as named by the service. */
  param?: string;
  label: string;
  /**
   * Applied by the landing page rather than by the user, as the service reports it. Such a filter
   * cannot be cleared from the URL, so a caller offering a remove button drops these; a caller
   * describing the whole search — the save dialog — keeps them.
   */
  locked: boolean;
};

/**
 * Every filter the response says is applied, as display labels.
 *
 * Derived from the facets rather than the URL: the response is what knows a value's localized
 * label, and on a landing page it is also the only place the path's own filters show up.
 *
 * Nothing is filtered out here — a caller that can only act on the removable ones (the chips above
 * the results) drops the locked ones itself, while a caller describing what is being saved wants
 * the page's filters too, because they end up in the saved search. Which is which is the service's
 * answer, carried on each filter as `locked`.
 */
export const getAppliedFilters = (
  facets: Facet[],
  facetLabel: (key: string) => string,
  /* Opt-in booleans have no label of their own — see `useFacetValueLabel`. Without this the chip
   * above the results, and the summary in the save dialog, both read "true". */
  facetValueLabel: (
    facetKey: string,
    value: { value: string; label?: string | null }
  ) => string,
  words: RangeWords
): AppliedFilter[] =>
  facets.flatMap((facet): AppliedFilter[] => {
    if (isTermsFacet(facet)) {
      return facet.values
        .filter(value => value.selected)
        .map(value => ({
          key: `${facet.key}:${value.value}`,
          facetKey: facet.key,
          kind: "terms" as const,
          value: value.value,
          label: describeTerms(
            facet.key,
            facetValueLabel(facet.key, value),
            facetLabel
          ),
          locked: value.locked,
        }));
    }

    /* The service's own label — "From 30%" — which is the whole filter in one phrase, so nothing
     * is assembled here. */
    if (isChoiceFacet(facet)) {
      return facet.values
        .filter(value => value.selected)
        .map(value => ({
          key: facet.key,
          facetKey: facet.key,
          kind: "choice" as const,
          value: value.value,
          param: facet.param,
          label: value.label,
          locked: value.locked,
        }));
    }

    const { min, max } = facet.selected ?? {};

    if (min == null && max == null) {
      return [];
    }

    return [
      {
        key: facet.key,
        facetKey: facet.key,
        kind: "range" as const,
        label: `${facetLabel(facet.key)}: ${describeRange(
          min,
          max,
          facet.unit,
          words
        )}`,
        locked: facet.selected?.locked ?? false,
      },
    ];
  });
